import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { buildShopScopedPath } from '../../../utils/adminShopContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// REMOVED: Manual timezone selection - now automatically determined from salon address
// See: backend/services/timezoneResolutionService.js

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const TimeAndHoursSettings = () => {
  const { shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    timezone: '',
    openTime: '09:00',
    closeTime: '18:00',
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get(buildShopScopedPath('/admin/settings/shop', shopId));
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(t('admin.settings_error_loading'));
    } finally {
      setLoading(false);
    }
  }, [shopId, t]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axiosClient.put(buildShopScopedPath('/admin/settings/shop', shopId), settings);
      toast.success(t('admin.settings_saved'));
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_error_saving');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateTimes = () => {
    const openTime = settings.openTime;
    const closeTime = settings.closeTime;
    
    if (openTime >= closeTime) {
      toast.error(t('admin.settings_time_validation'));
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (validateTimes()) {
      saveSettings();
    }
  };

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
          <CircularProgress size={32} style={{ color: ACCENT }} />
          <span>{t('admin.settings_loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('admin.settings_time_hours_title')}
        subtitle={t('admin.settings_time_hours_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        <div className="space-y-6">
          {/* Timezone Display (Read-Only) */}
          <div className="rounded-xl border border-white/20 dark:border-white/10 bg-gradient-to-br from-white/70 to-white/50 dark:from-zinc-800/50 dark:to-zinc-800/30 backdrop-blur-sm p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-5 w-5 text-[--accent]" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ '--accent': ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.settings_timezone_label')}
                </label>
                <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {settings.timezone || 'UTC'}
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {t('admin.settings_timezone_auto')}
                </p>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Open Time */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.settings_open_time')}
              </label>
              <input
                type="time"
                value={settings.openTime}
                onChange={(e) => handleInputChange('openTime', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
              <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">{t('admin.settings_open_time_hint')}</p>
            </div>

            {/* Close Time */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.settings_close_time')}
              </label>
              <input
                type="time"
                value={settings.closeTime}
                onChange={(e) => handleInputChange('closeTime', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
              <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">{t('admin.settings_close_time_hint')}</p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t('admin.settings_hours_note')}
          </p>
        </div>

        {/* Action Button */}
        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-4 border-t border-white/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: ACCENT }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                <span>{t('admin.settings_saving_changes')}</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('admin.settings_save_changes')}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

export default TimeAndHoursSettings;
